// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore

import axios from 'axios';

import {
  convertPathToGuardName,
  convertPathToLoginRoute,
} from '@/router/router';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const api = axios.create({
  responseType: 'json',
  baseURL: API_BASE_URL,
  headers: {
    'content-Type': 'application/json',
    Accept: 'application/json',
    // どのパスからリクエストが来たかをバックエンド側から判断するために使用する
    'X-Guard': convertPathToGuardName(window.location.pathname),
  },
  withCredentials: true,
});

export const HTTP_METHODS = {
  GET: 'get',
  POST: 'post',
  PUT: 'put',
  PATCH: 'patch',
  DELETE: 'delete',
} as const;

export type HttpMethod = (typeof HTTP_METHODS)[keyof typeof HTTP_METHODS];

export type ResDataObjectType<T = unknown, U = unknown> = {
  data: T;
  success: boolean;
  status: number;
  message?: string;
  errors?: U;
};
async function syncApi<T, U>(
  method: HttpMethod,
  url: string,
  params = {},
  headers = {}
): Promise<ResDataObjectType<T, U>> {
  try {
    const response = await api[method](url, params, headers);
    const { data, status } = response;
    return {
      data:
        data && Object.prototype.hasOwnProperty.call(data, 'data')
          ? data.data
          : data,
      success: true,
      status,
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const errorStatus =
        error.response?.data.code ?? error.response?.status ?? error.status;

      /**
       * ログインセッションが切れた場合はログイン画面に遷移する
       */
      if (errorStatus === 401) {
        window.location.href = convertPathToLoginRoute(
          window.location.pathname
        );
      }

      /**
       * トークンなし・一致しない場合は画面をリロードし、XSRF-TOKENトークンが付与されるようにする
       */
      if (errorStatus === 419) {
        window.location.reload();
      }

      return {
        data: [] as T,
        success: false,
        status: error.response?.status ?? 500,
        message: error.response?.data.message,
        errors: error.response?.data.errors,
      };
    }
    return {
      data: [] as T,
      success: false,
      status: 500,
      message: 'Internal Server Error',
    };
  }
}

export async function apiGet<T, U = Partial<Record<string, string>>>(
  url: string,
  params = {}
) {
  return await syncApi<T, U>(HTTP_METHODS.GET, `${API_BASE_URL}${url}`, params);
}

export async function apiPost<T, U = Partial<Record<string, string>>>(
  url: string,
  params = {}
) {
  return await syncApi<T, U>(
    HTTP_METHODS.POST,
    `${API_BASE_URL}${url}`,
    params
  );
}

export async function apiPatch<T, U = Partial<Record<string, string>>>(
  url: string,
  params = {}
) {
  return await syncApi<T, U>(
    HTTP_METHODS.PATCH,
    `${API_BASE_URL}${url}`,
    params
  );
}

export async function apiPut<T, U = Partial<Record<string, string>>>(
  url: string,
  params = {}
) {
  return await syncApi<T, U>(
    HTTP_METHODS.PUT,
    `${API_BASE_URL}${url}`,
    params
  );
}

export async function apiDelete<T, U = Partial<Record<string, string>>>(
  url: string,
  params = {}
) {
  return await syncApi<T, U>(HTTP_METHODS.DELETE, `${API_BASE_URL}${url}`, {
    data: params,
  });
}

export async function apiFile<T, U = Partial<Record<string, string>>>(
  url: string,
  params = {},
  method: HttpMethod = HTTP_METHODS.POST
) {
  return await syncApi<T, U>(
    HTTP_METHODS.POST,
    `${API_BASE_URL}${url}`,
    params,
    {
      headers: {
        'content-type': 'multipart/form-data',
        'X-HTTP-Method-Override': method,
        'X-Requested-With': 'XMLHttpRequest',
      },
      withCredentials: true,
    }
  );
}

export async function apiDownload<T, U = Partial<Record<string, string>>>(
  url: string,
  params = {}
) {
  return await syncApi<T, U>(
    HTTP_METHODS.POST,
    `${API_BASE_URL}${url}`,
    params,
    { responseType: 'arraybuffer' }
  );
}
